import { useEffect, useState } from "react"

import { Flex, Segmented, Typography } from "antd"

import InputComponent from "../../../../Components/Input"
import InputNumberComponent from "../../../../Components/Input/InputNumber"
import Private from "../../../../Components/Private"
import Icon from "../../../../Icon"
import { normalizeMoney } from "../../../../Utils/normalizeMoney"

import "./ThresholdInput.less"

const { Text } = Typography

// The caption is the only place the configured threshold is spelled out, so just the amount is
// wrapped in `Private` — the sentence around it stays readable in Incognito mode (CHA1-1354). The
// editable input itself is left alone so a threshold can still be set while Incognito is on.
const texts = {
  auto_refund: {
    all: "All alerts will be auto-refunded",
    allOptionLabel: "Refund all alerts",
    inputLabel: "Auto-refund amount range",
    getInputCaption: (amount) => (
      <>
        All alerts between $0.00 and <Private>{`$${(amount ?? 0).toFixed(2)}`}</Private> will be
        automatically refunded.
      </>
    )
  },
  auto_cancel: {
    all: "All alerted transactions will be auto-cancelled",
    allOptionLabel: "Cancel all subscriptions",
    inputLabel: "Auto-cancel threshold",
    getInputCaption: (amount) => (
      <>
        All subscriptions between $0.00 and <Private>{`$${(amount ?? 0).toFixed(2)}`}</Private> will
        be automatically canceled.
      </>
    )
  }
}

const ThresholdInput = ({
  initialThresholdAmount,
  value,
  onChange,
  formType = "auto_refund",
  onOptionChange,
  ...props
}) => {
  const [selectedOption, setSelectedOption] = useState(value === 0 ? "all" : "threshold")
  const [thresholdValue, setThresholdValue] = useState(normalizeMoney(value ?? 0))

  useEffect(() => {
    setThresholdValue(normalizeMoney(value ?? 0))
  }, [value])

  const handleOptionChange = (option) => {
    setSelectedOption(option)
    onOptionChange?.(option)
    if (option === "all") {
      setThresholdValue(normalizeMoney(0))
      onChange(0)
    } else if (option === "threshold" && value === 0) {
      const nextValue = initialThresholdAmount || 500
      setThresholdValue(normalizeMoney(nextValue))
      onChange(nextValue)
    }
  }

  const handleThresholdChange = (e) => {
    setThresholdValue(e.target.value)
  }

  const handleThresholdBlur = (e) => {
    const nextValue = normalizeMoney(e.target.value) || normalizeMoney(0)

    setThresholdValue(nextValue)
    onChange(Number(nextValue))
  }

  return (
    <div className="threshold-input">
      <Segmented
        options={[
          {
            label: texts[formType].allOptionLabel,
            value: "all"
          },
          { label: "Set custom threshold", value: "threshold" }
        ]}
        value={selectedOption}
        onChange={handleOptionChange}
        className="option-switcher"
      />
      {selectedOption === "all" && (
        <Flex vertical gap={9} align="center" justify="center" className="all-option-content">
          <Icon type="circle_check_green" className="all-option-icon" />
          <Text strong>{texts[formType].all}</Text>
        </Flex>
      )}
      {selectedOption === "threshold" && (
        <Flex className="input-container" gap={8} vertical>
          <Text strong>{texts[formType].inputLabel}</Text>
          <Flex gap={8} align="center">
            <InputNumberComponent
              addonBefore={<Text type="secondary">$</Text>}
              min={0}
              step={10}
              precision={2}
              style={{ width: "100%" }}
              disabled={true}
              value={0}
            />
            <div className="dash" />
            <InputComponent
              inputMode="decimal"
              style={{ width: "100%" }}
              value={thresholdValue}
              onChange={handleThresholdChange}
              onBlur={handleThresholdBlur}
            />
          </Flex>
          <Text type="secondary">{texts[formType].getInputCaption(value)}</Text>
        </Flex>
      )}
    </div>
  )
}

export default ThresholdInput
